/**
 * CompetencySetEditor Component
 * Accordion-based editor for competency sets - grouped by Value
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  useGetCompetencySetByIdQuery,
  useUpdateCompetencySetMutation,
} from "../../../services/api";
import LearningPickerModal from "./LearningPickerModal";
import DevelopmentGoalsTab from "./DevelopmentGoalsTab";
import "./CompetencySetEditor.css";

// ============================================
// CONSTANTS
// ============================================
const AVAILABLE_ROLES = [
  "Executive / C-Suite",
  "Senior Director / Director",
  "Manager",
  "Staff",
];

const ROLE_COLORS = {
  "Executive / C-Suite": { bg: "#ffcfc9", text: "#9a2617" },
  "Senior Director / Director": { bg: "#d4e4fc", text: "#1e4a8a" },
  "Manager": { bg: "#d4f5e9", text: "#0e5c3c" },
  "Staff": { bg: "#fef3c7", text: "#92400e" },
};

// ============================================
// ICONS
// ============================================
const IconBack = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const IconChevron = ({ isExpanded }) => (
  <svg
    width="20" height="20" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    style={{ transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const IconTrash = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const IconPlus = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

// ============================================
// SUB-COMPONENTS
// ============================================

/**
 * EditableCell - Inline editable text cell
 */
function EditableCell({ value, onSave, placeholder, className, multiline }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || "");
  const inputRef = useRef(null);

  useEffect(() => {
    setEditValue(value || "");
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleClick = (e) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (editValue !== value) {
      onSave(editValue);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !multiline) {
      e.preventDefault();
      inputRef.current.blur();
    }
    if (e.key === "Escape") {
      setEditValue(value || "");
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <div className={`cse-editable-cell cse-editing ${className || ""}`} onClick={(e) => e.stopPropagation()}>
        {multiline ? (
          <textarea
            ref={inputRef}
            className="cse-cell-input"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={2}
          />
        ) : (
          <input
            ref={inputRef}
            type="text"
            className="cse-cell-input"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={`cse-editable-cell ${className || ""}`}
      onClick={handleClick}
    >
      {value ? (
        <span>{value}</span>
      ) : (
        <span className="cse-cell-placeholder">{placeholder || "Click to edit"}</span>
      )}
    </div>
  );
}

/**
 * RoleBadge - Display a role with color
 */
function RoleBadge({ role }) {
  const colors = ROLE_COLORS[role] || { bg: "#e5e7eb", text: "#374151" };
  return (
    <span
      className="cse-role-badge"
      style={{ backgroundColor: colors.bg, color: colors.text }}
    >
      {role.split(" / ")[0]}
    </span>
  );
}

/**
 * RolesEditor - Modal for editing roles
 */
function RolesEditor({ roles, onSave, onClose }) {
  const [selected, setSelected] = useState(roles || []);

  const handleToggle = (role) => {
    setSelected((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const handleSave = () => {
    onSave(selected);
    onClose();
  };

  return (
    <div className="cse-roles-modal" onClick={onClose}>
      <div className="cse-roles-modal-content" onClick={(e) => e.stopPropagation()}>
        <h3>Select Roles</h3>
        {AVAILABLE_ROLES.map((role) => (
          <label key={role} className="cse-role-option">
            <input
              type="checkbox"
              checked={selected.includes(role)}
              onChange={() => handleToggle(role)}
            />
            <span>{role}</span>
          </label>
        ))}
        <div className="cse-roles-modal-actions">
          <button className="cd-btn cd-btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="cd-btn cd-btn-primary" onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * LearningCell - Display and edit learning resources (array)
 */
function LearningCell({ learning = [], onAdd, onRemove }) {
  return (
    <div className="cse-learning-list">
      {learning.map((resource, idx) => (
        <div key={idx} className="cse-learning-item">
          <span className="cse-learning-text">
            <strong>{resource.type}:</strong> {resource.title}
          </span>
          <button
            className="cse-learning-remove"
            onClick={() => onRemove(idx)}
            title="Remove"
          >
            ×
          </button>
        </div>
      ))}
      <button className="cse-learning-browse" onClick={onAdd}>
        Browse Library →
      </button>
    </div>
  );
}

/**
 * Deep clone object
 */
function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Normalize learning field to always be an array
 * Handles auto-migration from single object to array format
 */
function normalizeLearning(learning) {
  if (!learning) return [];
  if (Array.isArray(learning)) return learning;
  // Single object - wrap in array
  return [learning];
}

/**
 * Normalize all learning fields in values array
 */
function normalizeValuesData(values) {
  return values.map(value => ({
    ...value,
    competencies: value.competencies?.map(competency => ({
      ...competency,
      actions: competency.actions?.map(action => ({
        ...action,
        entries: action.entries?.map(entry => ({
          ...entry,
          learning: normalizeLearning(entry.learning),
        })),
      })),
    })),
  }));
}

/**
 * Flatten value data for table display (without Value column)
 * Includes special row types for inline "Add" buttons
 */
function flattenValueData(value, vIdx) {
  const rows = [];

  if (!value.competencies || value.competencies.length === 0) {
    return rows;
  }

  let isFirstRowForCompetency = true;

  value.competencies.forEach((competency, cIdx) => {
    isFirstRowForCompetency = true;

    if (!competency.actions || competency.actions.length === 0) {
      // Competency with no actions - show competency name row
      rows.push({
        rowType: 'entry',
        vIdx,
        cIdx,
        aIdx: null,
        eIdx: null,
        competencyName: competency.competencyName,
        actionName: null,
        skill: null,
        attitude: null,
        roles: [],
        learning: null,
        isFirstInCompetency: true,
        isFirstInAction: false,
        isEmpty: true,
      });
      isFirstRowForCompetency = false;
    } else {
      competency.actions.forEach((action, aIdx) => {
        if (!action.entries || action.entries.length === 0) {
          // Action with no entries - show action name row
          rows.push({
            rowType: 'entry',
            vIdx,
            cIdx,
            aIdx,
            eIdx: null,
            competencyName: competency.competencyName,
            actionName: action.actionName,
            skill: null,
            attitude: null,
            roles: [],
            learning: null,
            isFirstInCompetency: isFirstRowForCompetency,
            isFirstInAction: true,
            isEmpty: true,
          });
          isFirstRowForCompetency = false;
        } else {
          // Render all entries for this action
          action.entries.forEach((entry, eIdx) => {
            rows.push({
              rowType: 'entry',
              vIdx,
              cIdx,
              aIdx,
              eIdx,
              competencyName: competency.competencyName,
              actionName: action.actionName,
              skill: entry.skill,
              attitude: entry.attitude,
              roles: entry.roles || [],
              learning: entry.learning,
              isFirstInCompetency: isFirstRowForCompetency,
              isFirstInAction: eIdx === 0,
              isEmpty: false,
            });
            isFirstRowForCompetency = false;
          });
        }

        // Add "Add Entry" row after each action's entries
        rows.push({
          rowType: 'add-entry',
          vIdx,
          cIdx,
          aIdx,
          actionName: action.actionName,
        });
      });
    }

    // Add "Add Action" row after each competency's actions
    rows.push({
      rowType: 'add-action',
      vIdx,
      cIdx,
      competencyName: competency.competencyName,
    });
  });

  return rows;
}

/**
 * Count entries in a value
 */
function countEntries(value) {
  let count = 0;
  value.competencies?.forEach((c) => {
    c.actions?.forEach((a) => {
      count += a.entries?.length || 0;
    });
  });
  return count;
}

// ============================================
// MAIN COMPONENT
// ============================================

export default function CompetencySetEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Get the "from" path if navigated from a company page
  const backPath = location.state?.from || "/admin/competency-sets";
  const backLabel = location.state?.fromLabel || "Competency Sets";

  // API hooks
  const { data, isLoading, error } = useGetCompetencySetByIdQuery(id);
  const [updateSet, { isLoading: isSaving }] = useUpdateCompetencySetMutation();

  // Local state
  const [values, setValues] = useState([]);
  const [expandedValues, setExpandedValues] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [rolesModal, setRolesModal] = useState({ isOpen: false, path: null, roles: [] });
  const [learningModal, setLearningModal] = useState({ isOpen: false, path: null });
  const [activeTab, setActiveTab] = useState("rubric");

  // Initialize local state from API data
  useEffect(() => {
    if (data?.values) {
      // Normalize learning fields to arrays (auto-migration)
      const normalizedValues = normalizeValuesData(deepClone(data.values));
      setValues(normalizedValues);
      // Expand all values by default
      const expanded = {};
      data.values.forEach((_, idx) => {
        expanded[idx] = true;
      });
      setExpandedValues(expanded);
      setHasChanges(false);
    }
  }, [data]);

  // ============================================
  // ACCORDION HANDLERS
  // ============================================

  const toggleValue = (vIdx) => {
    setExpandedValues((prev) => ({
      ...prev,
      [vIdx]: !prev[vIdx],
    }));
  };

  const expandAll = () => {
    const expanded = {};
    values.forEach((_, idx) => {
      expanded[idx] = true;
    });
    setExpandedValues(expanded);
  };

  const collapseAll = () => {
    setExpandedValues({});
  };

  // ============================================
  // UPDATE HANDLERS
  // ============================================

  const updateValue = useCallback((vIdx, newValue) => {
    setValues((prev) => {
      const updated = deepClone(prev);
      updated[vIdx].valueName = newValue;
      return updated;
    });
    setHasChanges(true);
  }, []);

  const updateCompetency = useCallback((vIdx, cIdx, newValue) => {
    setValues((prev) => {
      const updated = deepClone(prev);
      updated[vIdx].competencies[cIdx].competencyName = newValue;
      return updated;
    });
    setHasChanges(true);
  }, []);

  const updateAction = useCallback((vIdx, cIdx, aIdx, newValue) => {
    setValues((prev) => {
      const updated = deepClone(prev);
      updated[vIdx].competencies[cIdx].actions[aIdx].actionName = newValue;
      return updated;
    });
    setHasChanges(true);
  }, []);

  const updateEntry = useCallback((vIdx, cIdx, aIdx, eIdx, field, newValue) => {
    setValues((prev) => {
      const updated = deepClone(prev);
      updated[vIdx].competencies[cIdx].actions[aIdx].entries[eIdx][field] = newValue;
      return updated;
    });
    setHasChanges(true);
  }, []);

  // ============================================
  // ADD HANDLERS
  // ============================================

  const addValue = useCallback(() => {
    setValues((prev) => {
      const newIdx = prev.length;
      setExpandedValues((exp) => ({ ...exp, [newIdx]: true }));
      return [...prev, { valueName: "New Value", competencies: [] }];
    });
    setHasChanges(true);
  }, []);

  const addCompetency = useCallback((vIdx) => {
    setValues((prev) => {
      const updated = deepClone(prev);
      updated[vIdx].competencies.push({ competencyName: "New Competency", actions: [] });
      return updated;
    });
    setHasChanges(true);
  }, []);

  const addAction = useCallback((vIdx, cIdx) => {
    setValues((prev) => {
      const updated = deepClone(prev);
      updated[vIdx].competencies[cIdx].actions.push({ actionName: "New Action", entries: [] });
      return updated;
    });
    setHasChanges(true);
  }, []);

  const addEntry = useCallback((vIdx, cIdx, aIdx) => {
    setValues((prev) => {
      const updated = deepClone(prev);
      updated[vIdx].competencies[cIdx].actions[aIdx].entries.push({
        skill: "",
        attitude: "",
        roles: [],
        learning: [],
      });
      return updated;
    });
    setHasChanges(true);
  }, []);

  // ============================================
  // DELETE HANDLERS
  // ============================================

  const deleteValue = useCallback((vIdx) => {
    setValues((prev) => prev.filter((_, idx) => idx !== vIdx));
    setHasChanges(true);
  }, []);

  const deleteCompetency = useCallback((vIdx, cIdx) => {
    setValues((prev) => {
      const updated = deepClone(prev);
      updated[vIdx].competencies = updated[vIdx].competencies.filter((_, idx) => idx !== cIdx);
      return updated;
    });
    setHasChanges(true);
  }, []);

  const deleteAction = useCallback((vIdx, cIdx, aIdx) => {
    setValues((prev) => {
      const updated = deepClone(prev);
      updated[vIdx].competencies[cIdx].actions = updated[vIdx].competencies[cIdx].actions.filter(
        (_, idx) => idx !== aIdx
      );
      return updated;
    });
    setHasChanges(true);
  }, []);

  const deleteEntry = useCallback((vIdx, cIdx, aIdx, eIdx) => {
    setValues((prev) => {
      const updated = deepClone(prev);
      updated[vIdx].competencies[cIdx].actions[aIdx].entries = updated[vIdx].competencies[cIdx].actions[aIdx].entries.filter(
        (_, idx) => idx !== eIdx
      );
      return updated;
    });
    setHasChanges(true);
  }, []);

  // ============================================
  // SAVE
  // ============================================

  const handleSave = async () => {
    try {
      await updateSet({
        id,
        values,
      }).unwrap();
      setHasChanges(false);
    } catch (err) {
      console.error("Failed to save:", err);
    }
  };

  // ============================================
  // ROLES MODAL
  // ============================================

  const openRolesModal = (vIdx, cIdx, aIdx, eIdx, currentRoles) => {
    setRolesModal({
      isOpen: true,
      path: { vIdx, cIdx, aIdx, eIdx },
      roles: currentRoles || [],
    });
  };

  const handleRolesSave = (newRoles) => {
    const { vIdx, cIdx, aIdx, eIdx } = rolesModal.path;
    updateEntry(vIdx, cIdx, aIdx, eIdx, "roles", newRoles);
    setRolesModal({ isOpen: false, path: null, roles: [] });
  };

  // ============================================
  // LEARNING MODAL
  // ============================================

  const openLearningModal = (vIdx, cIdx, aIdx, eIdx) => {
    setLearningModal({
      isOpen: true,
      path: { vIdx, cIdx, aIdx, eIdx },
    });
  };

  const handleLearningSelect = (resource) => {
    const { vIdx, cIdx, aIdx, eIdx } = learningModal.path;
    setValues((prev) => {
      const updated = deepClone(prev);
      const entry = updated[vIdx].competencies[cIdx].actions[aIdx].entries[eIdx];

      // Initialize array if needed
      if (!entry.learning) entry.learning = [];

      // Add resource to array (avoid duplicates by title)
      const exists = entry.learning.some(r => r.title === resource.title);
      if (!exists) {
        entry.learning.push({
          title: resource.title,
          url: resource.url,
          type: resource.type,
          description: resource.description,
        });
      }

      return updated;
    });
    setHasChanges(true);
    setLearningModal({ isOpen: false, path: null });
  };

  const handleLearningRemove = (vIdx, cIdx, aIdx, eIdx, resourceIndex) => {
    setValues((prev) => {
      const updated = deepClone(prev);
      const entry = updated[vIdx].competencies[cIdx].actions[aIdx].entries[eIdx];
      entry.learning.splice(resourceIndex, 1);
      return updated;
    });
    setHasChanges(true);
  };

  // ============================================
  // RENDER
  // ============================================

  if (isLoading) {
    return <div className="cse-loading">Loading competency set...</div>;
  }

  if (error) {
    return (
      <div className="cse-error">
        Error loading competency set. Please try again.
      </div>
    );
  }

  const totalEntries = values.reduce((sum, v) => sum + countEntries(v), 0);
  const totalCompetencies = values.reduce((sum, v) => sum + (v.competencies?.length || 0), 0);

  return (
    <div className="cse-page">
      {/* Header */}
      <div className="cse-header">
        <div className="cse-header-left">
          <button className="cse-back-btn" onClick={() => navigate(backPath)}>
            <IconBack /> Back to {backLabel}
          </button>
          <h1 className="cse-title">{data?.name || "Competency Set"}</h1>
          <p className="cse-subtitle">Edit values, competencies, actions, and entries below</p>
        </div>
        <div className="cse-header-actions">
          {hasChanges && <span className="cse-changes-badge">Unsaved changes</span>}
          <button
            className="cse-save-btn"
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="cse-tabs">
        <button
          className={`cse-tab ${activeTab === "rubric" ? "cse-tab-active" : ""}`}
          onClick={() => setActiveTab("rubric")}
        >
          Rubric Editor
        </button>
        <button
          className={`cse-tab ${activeTab === "development" ? "cse-tab-active" : ""}`}
          onClick={() => setActiveTab("development")}
        >
          Recommended Plans
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === "rubric" && (
        <>
          {/* Expand/Collapse All */}
      {values.length > 0 && (
        <div className="cse-toolbar">
          <button className="cse-toolbar-btn" onClick={expandAll}>Expand All</button>
          <button className="cse-toolbar-btn" onClick={collapseAll}>Collapse All</button>
        </div>
      )}

      {/* Accordion Sections */}
      <div className="cse-accordion-container">
        {values.map((value, vIdx) => {
          const isExpanded = expandedValues[vIdx];
          const rows = flattenValueData(value, vIdx);
          const entryCount = countEntries(value);

          return (
            <div key={vIdx} className={`cse-accordion ${isExpanded ? "cse-accordion-expanded" : ""}`}>
              {/* Accordion Header */}
              <div className="cse-accordion-header" onClick={() => toggleValue(vIdx)}>
                <div className="cse-accordion-toggle">
                  <IconChevron isExpanded={isExpanded} />
                </div>
                <div className="cse-accordion-title" onClick={(e) => e.stopPropagation()}>
                  <EditableCell
                    value={value.valueName}
                    onSave={(val) => updateValue(vIdx, val)}
                    placeholder="Value name"
                    className="cse-value-name"
                  />
                </div>
                <span className="cse-accordion-badge">{entryCount} entries</span>
                <button
                  className="cse-accordion-delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteValue(vIdx);
                  }}
                  title="Delete value"
                >
                  <IconTrash />
                </button>
              </div>

              {/* Accordion Body */}
              {isExpanded && (
                <div className="cse-accordion-body">
                  {rows.length === 0 ? (
                    <div className="cse-empty-value">
                      <p>No competencies yet. Add one below.</p>
                    </div>
                  ) : (
                    <div className="cse-table-wrapper">
                      <table className="cse-table">
                        <thead>
                          <tr>
                            <th className="cse-th-competency">Competency</th>
                            <th className="cse-th-action">Action</th>
                            <th className="cse-th-skill">Skill</th>
                            <th className="cse-th-attitude">Attitude</th>
                            <th className="cse-th-roles">Roles</th>
                            <th className="cse-th-learning">Learning</th>
                            <th className="cse-th-actions"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row, idx) => {
                            // Add Entry row
                            if (row.rowType === 'add-entry') {
                              return (
                                <tr key={`add-entry-${row.cIdx}-${row.aIdx}`} className="cse-add-row">
                                  <td></td>
                                  <td></td>
                                  <td colSpan="5">
                                    <button
                                      className="cse-inline-add-btn"
                                      onClick={() => addEntry(row.vIdx, row.cIdx, row.aIdx)}
                                    >
                                      <IconPlus /> Add Entry
                                    </button>
                                  </td>
                                </tr>
                              );
                            }

                            // Add Action row
                            if (row.rowType === 'add-action') {
                              return (
                                <tr key={`add-action-${row.cIdx}`} className="cse-add-row cse-add-action-row">
                                  <td></td>
                                  <td colSpan="6">
                                    <button
                                      className="cse-inline-add-btn"
                                      onClick={() => addAction(row.vIdx, row.cIdx)}
                                    >
                                      <IconPlus /> Add Action
                                    </button>
                                  </td>
                                </tr>
                              );
                            }

                            // Regular entry row
                            return (
                              <tr
                                key={idx}
                                className={row.isFirstInCompetency ? "cse-row-competency-start" : ""}
                              >
                                {/* Competency */}
                                <td className="cse-td-competency">
                                  {row.isFirstInCompetency && (
                                    <EditableCell
                                      value={row.competencyName}
                                      onSave={(val) => updateCompetency(vIdx, row.cIdx, val)}
                                      placeholder="Competency name"
                                      className="cse-cell-competency"
                                    />
                                  )}
                                </td>

                                {/* Action */}
                                <td>
                                  {row.isFirstInAction && row.aIdx !== null && (
                                    <EditableCell
                                      value={row.actionName}
                                      onSave={(val) => updateAction(vIdx, row.cIdx, row.aIdx, val)}
                                      placeholder="Action name"
                                      className="cse-cell-action"
                                    />
                                  )}
                                </td>

                                {/* Skill */}
                                <td>
                                  {row.eIdx !== null && (
                                    <EditableCell
                                      value={row.skill}
                                      onSave={(val) => updateEntry(vIdx, row.cIdx, row.aIdx, row.eIdx, "skill", val)}
                                      placeholder="Skill"
                                      className="cse-cell-text"
                                      multiline
                                    />
                                  )}
                                </td>

                                {/* Attitude */}
                                <td>
                                  {row.eIdx !== null && (
                                    <EditableCell
                                      value={row.attitude}
                                      onSave={(val) => updateEntry(vIdx, row.cIdx, row.aIdx, row.eIdx, "attitude", val)}
                                      placeholder="Attitude"
                                      className="cse-cell-text"
                                      multiline
                                    />
                                  )}
                                </td>

                                {/* Roles */}
                                <td>
                                  {row.eIdx !== null && (
                                    <div className="cse-roles-display">
                                      {row.roles.length > 0 ? (
                                        <>
                                          {row.roles.map((role, rIdx) => (
                                            <RoleBadge key={rIdx} role={role} />
                                          ))}
                                          <button
                                            className="cse-roles-edit-btn"
                                            onClick={() => openRolesModal(vIdx, row.cIdx, row.aIdx, row.eIdx, row.roles)}
                                          >
                                            Edit
                                          </button>
                                        </>
                                      ) : (
                                        <button
                                          className="cse-roles-edit-btn"
                                          onClick={() => openRolesModal(vIdx, row.cIdx, row.aIdx, row.eIdx, [])}
                                        >
                                          + Roles
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </td>

                                {/* Learning */}
                                <td>
                                  {row.eIdx !== null && (
                                    <LearningCell
                                      learning={row.learning || []}
                                      onAdd={() => openLearningModal(vIdx, row.cIdx, row.aIdx, row.eIdx)}
                                      onRemove={(resourceIdx) => handleLearningRemove(vIdx, row.cIdx, row.aIdx, row.eIdx, resourceIdx)}
                                    />
                                  )}
                                </td>

                                {/* Actions */}
                                <td>
                                  <div className="cse-row-actions">
                                    {row.eIdx !== null && (
                                      <button
                                        className="cse-delete-btn"
                                        onClick={() => deleteEntry(vIdx, row.cIdx, row.aIdx, row.eIdx)}
                                        title="Delete entry"
                                      >
                                        <IconTrash />
                                      </button>
                                    )}
                                    {row.isEmpty && row.aIdx !== null && (
                                      <button
                                        className="cse-delete-btn"
                                        onClick={() => deleteAction(vIdx, row.cIdx, row.aIdx)}
                                        title="Delete action"
                                      >
                                        <IconTrash />
                                      </button>
                                    )}
                                    {row.isEmpty && row.aIdx === null && (
                                      <button
                                        className="cse-delete-btn"
                                        onClick={() => deleteCompetency(vIdx, row.cIdx)}
                                        title="Delete competency"
                                      >
                                        <IconTrash />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Add Competency button at bottom */}
                  <div className="cse-accordion-actions">
                    <button className="cse-add-btn" onClick={() => addCompetency(vIdx)}>
                      <IconPlus /> Add Competency
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Add New Value Button */}
        <button className="cse-add-value-btn" onClick={addValue}>
          <IconPlus /> Add New Value
        </button>
      </div>

      {/* Summary */}
      <div className="cse-summary">
        <span>
          <strong>{values.length}</strong> Values
        </span>
        <span className="cse-summary-divider">|</span>
        <span>
          <strong>{totalCompetencies}</strong> Competencies
        </span>
        <span className="cse-summary-divider">|</span>
        <span>
          <strong>{totalEntries}</strong> Entries
        </span>
      </div>
        </>
      )}

      {/* Development Plan Tab */}
      {activeTab === "development" && (
        <DevelopmentGoalsTab setId={id} values={values} />
      )}

      {/* Roles Modal */}
      {rolesModal.isOpen && (
        <RolesEditor
          roles={rolesModal.roles}
          onSave={handleRolesSave}
          onClose={() => setRolesModal({ isOpen: false, path: null, roles: [] })}
        />
      )}

      {/* Learning Picker Modal */}
      {learningModal.isOpen && (
        <LearningPickerModal
          onSelect={handleLearningSelect}
          onClose={() => setLearningModal({ isOpen: false, path: null })}
        />
      )}
    </div>
  );
}
